import { FC, useState, useEffect, useMemo } from 'react';
import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import { OTHER_TYPE_ID, OTHER_TYPE_INPUT_TEXT, OTHER_TYPE_NAME, setComValue } from '@/common/components/business/DynamicComponent/config';
import { deepCopy, isArray, isNotEmpty } from '@lhb/func';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import { changeDynamicOtherFormNameArr } from '@/store/common';
import { useDispatch, useSelector } from 'react-redux';
import { customEstimateSalesKWK } from '@/common/components/business/ExpandStore/ts-config';
import styles from './index.module.less';
import { useMethods } from '@lhb/hook';
interface InputInfoI {
  visible:boolean;// 是否展示
  value:string;// input表单值
}

/**
 * @Description 单选组件
 */
const Radio: FC<{
  propertyItem: any;
  form: any;
  disabled?: boolean;
  required?: boolean;
  /** 清除单选后的回调 */
  onClear?: any;
  /** 单选、多选、输入框等值变更时的提示内容 */
  showHintStr?: any;
  salesFactorOfKWK?: any;
  /** 卡旺卡定制逻辑-预计销售额 */
  setSalesFactorOfKWK?: any;
}> = ({
  propertyItem,
  form,
  disabled,
  required,
  onClear,
  showHintStr,
  salesFactorOfKWK,
  setSalesFactorOfKWK, // 卡旺卡定制逻辑-预计销售额
}) => {
  const dispatch = useDispatch();
  const dynamicOtherFormNameArr = useSelector((state: any) => state.common.dynamicOtherFormNameArr); // 存放“其他”表单的formName
  const [helpInfo, setHelpInfo] = useState<string>('');
  const [selectValue, setSelectValue] = useState<any>(null);// 保存radio值
  const [inputInfo, setInputInfo] = useState<InputInfoI>({
    visible: false,
    value: ''
  });
  const label = propertyItem.anotherName || propertyItem.name || '';
  const restriction = propertyItem.restriction ? JSON.parse(propertyItem.restriction) : {};
  const placeholder = restriction.placeholder ? restriction.placeholder : `请选择${label || ''}`;
  /**
   * 子表单格式需要支持 identification 传入数组形式，如 ['subForm', 0, 'radio']
   * 因为子表单内的数组形式暂时不考虑 customEstimateSalesKWK[identification]，如果需要，后续可以判断转为字符串
   */
  const identification = propertyItem.identification;
  /** 如果 identification 是数组格式，那么“其他”输入框的 prop 也是数组，将数组最后一项拼接 _otherTypeInputText，如 ['subForm', 0, 'radio' + OTHER_TYPE_INPUT_TEXT] */
  const otherTypeInputTextName = Array.isArray(identification)
    ? identification.slice(0, -1).concat(identification[identification.length - 1] + OTHER_TYPE_INPUT_TEXT)
    : `${identification}${OTHER_TYPE_INPUT_TEXT}`;

  const options = useMemo(() => {
    if (!propertyItem) return;
    const _options = Array.isArray(propertyItem.propertyConfigOptionVOList) ? propertyItem.propertyConfigOptionVOList.map((itm) => ({
      label: itm.name,
      value: itm.id,
    })) : [];

    // 根据restriction中有withOther判断是否存在“其他”选项，
    if (restriction?.withOther && !_options.map((item) => item.id).includes(OTHER_TYPE_ID)) {
      _options.push({
        label: OTHER_TYPE_NAME,
        value: OTHER_TYPE_ID,
      });
      dispatch(changeDynamicOtherFormNameArr(Array.from(new Set([...dynamicOtherFormNameArr, otherTypeInputTextName]))));
    }
    return _options;
  }, []);

  const rules = [
    { required, message: placeholder },
  ];

  // 回显
  useEffect(() => {
    // 使用更通用的方式获取选中项和“其他”
    const selectedId = form.getFieldValue(identification);
    const otherTypeInputText = form.getFieldValue(otherTypeInputTextName);
    if (isNotEmpty(selectedId)) {
      methods.changeHandle({ target: { value: selectedId } });
      if (selectedId === OTHER_TYPE_ID) {
        form.setFieldValue(otherTypeInputTextName, otherTypeInputText);
      }
    }
  }, [propertyItem]);


  useEffect(() => {
    if (inputInfo.visible && inputInfo.value) {
      propertyItem[OTHER_TYPE_INPUT_TEXT] = inputInfo.value;
      setComValue(propertyItem, selectValue);
    }
  }, [inputInfo.value, selectValue]);

  const methods = useMethods({

    onClear: () => {
      setComValue(propertyItem, null);
      onClear?.(propertyItem);
      setInputInfo({
        visible: false,
        value: ''
      });
      setSelectValue(null);
      if (salesFactorOfKWK && customEstimateSalesKWK[identification]) {
        const _copyValue = deepCopy(salesFactorOfKWK);
        Reflect.deleteProperty(_copyValue, identification);
        setSalesFactorOfKWK && setSalesFactorOfKWK(_copyValue);
      }
    },

    changeHandle: (e: any) => {
      const { target } = e || {};
      const { value } = target || {};
      setSelectValue(value);
      if (salesFactorOfKWK && customEstimateSalesKWK[identification]) {
        const targetFields = customEstimateSalesKWK[identification]; // 该字段对应的所有影响因子
        const targetOption = options?.find((item) => item.value === value); // 选项值对应的影响因子
        const targetFactorOption = targetFields?.find((item) => item.name === targetOption?.label);
        targetFactorOption && setSalesFactorOfKWK && setSalesFactorOfKWK((state) => ({ ...state, [identification]: targetFactorOption.value }));
      }
      // 是否选中了“其他”
      if (value === OTHER_TYPE_ID) {
        setInputInfo({
          visible: true,
          value: ''
        });

      } else {
        setInputInfo({
          visible: false,
          value: ''
        });
      }

      const { restriction } = propertyItem || {};
      setHelpInfo(''); // 清空
      // 取消选中或者没有配置时不继续执行
      if (!restriction) return;
      const parsedRestriction = JSON.parse(restriction);
      const { redMark } = parsedRestriction || {};
      if (!(isArray(redMark) && value)) return; // 注意这里预期id不会为0
      const targetConfig = redMark.find((item: any) => item.optionId === value);
      if (targetConfig) {
        const { isRedMark, isItemTip, itemTip, isPageTip } = targetConfig;
        isRedMark && isItemTip && itemTip && (setHelpInfo(itemTip));
        isPageTip && showHintStr && (showHintStr());
      }
    },

  });

  return (
    <>
      <V2FormRadio
        className={styles.radio}
        label={label}
        name={identification}
        options={options}
        required={required}
        rules={rules}
        form={form}
        disabled={disabled}
        canClearEmpty={!required && !disabled}
        onClear={methods.onClear}
        onChange={methods.changeHandle}
        formItemConfig={{
          help: helpInfo ? <div className='c-f23'>{helpInfo}</div> : null,
        }}
      />
      {selectValue === OTHER_TYPE_ID && <V2FormInput
        className={styles.radioOther}
        name={otherTypeInputTextName}
        maxLength={50}
        onChange={(e) => setInputInfo((state) => ({
          ...state,
          value: e.target.value
        }))}
        rules={[{ required: true, message: '请输入其他选项值' }]}
        placeholder='请输入其他选项值'
      />}
    </>
  );
};

export default Radio;

